import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';
import { alpha, Box, useTheme } from '@/ui/primitives';
import { resolveChartPlotAreaBackgroundColor } from '@/ui/primitives/chartPlotAreaBackgroundColor';
import { LineChart } from '@/ui/primitives/LineChart';
import { chartPanelSurfaceBackgroundColor } from './chartPanelSurfaceTokens';
import { resolveMiniMetricFallbackLineColor } from './statusMetricAccentColors';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';

type MiniMetricLineChartProps = {
  data: MiniMetricLineChartDatum[];
  metricKey: string;
  color?: string;
  backgroundColor?: string;
  showPlotAreaBackground?: boolean;
  showPlotAreaFrame?: boolean;
  height: number;
  marginLeft: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  yAxisWidth: number;
  axisStrokeWidth?: number;
  hideXAxisLabels?: boolean;
  hideYAxisLabels?: boolean;
  hideXAxisTicks?: boolean;
  hideYAxisTicks?: boolean;
  showAllXAxisLabels?: boolean;
  xAxisHeight?: number;
  yAxisTickNumber?: number;
  yAxisTickInterval?: number[];
  yDomainPaddingRatio?: number;
  yDomainPaddingTopRatio?: number;
  yDomainPaddingBottomRatio?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  showHorizontalGrid?: boolean;
  hideXAxisLine?: boolean;
  hideYAxisLine?: boolean;
  tooltipLabelFormatter?: (label: string) => string;
  onSelectBar?: (dataIndex: number) => void;
  lineStrokeWidth?: number;
  dashedSegmentOpacity?: number;
};

type PlotBounds = {
  width: number;
  height: number;
};

type PathPoint = {
  x: number;
  y: number;
};

type MeasuredPoint = PathPoint & {
  key: string;
};

type StyledSegment = {
  dashed: boolean;
  points: PathPoint[];
};

const percentageMetricKeys = new Set(['flakeScore', 'passRate', 'value']);

const isPercentageMetric = (metricKey: string): boolean => percentageMetricKeys.has(metricKey);

const resolveDomain = (
  data: MiniMetricLineChartDatum[],
  metricKey: MiniMetricLineChartProps['metricKey'],
  yDomainPaddingRatio: number,
  yDomainPaddingTopRatio: number | undefined,
  yDomainPaddingBottomRatio: number | undefined,
  yAxisMinOverride: number | undefined,
  yAxisMaxOverride: number | undefined,
): { min: number; max: number } => {
  const values = data.map((point) => point.carryForwardValue).filter(Number.isFinite);
  const seriesMin = values.length > 0 ? Math.min(...values) : 0;
  const seriesMax = values.length > 0 ? Math.max(...values) : 0;
  const rawYAxisMin = Number.isFinite(seriesMin) ? Math.min(0, seriesMin) : 0;
  const rawYAxisMax = isPercentageMetric(metricKey) ? 100 : Math.max(seriesMax, 0);
  const yAxisRange = rawYAxisMax - rawYAxisMin;
  const yAxisPaddingBase =
    yAxisRange > 0 ? yAxisRange : rawYAxisMax !== 0 ? Math.abs(rawYAxisMax) : 1;
  const resolvedTopPaddingRatio = yDomainPaddingTopRatio ?? yDomainPaddingRatio;
  const resolvedBottomPaddingRatio = yDomainPaddingBottomRatio ?? yDomainPaddingRatio;
  const yAxisTopPadding =
    resolvedTopPaddingRatio > 0 ? yAxisPaddingBase * resolvedTopPaddingRatio : 0;
  const yAxisBottomPadding =
    resolvedBottomPaddingRatio > 0 ? yAxisPaddingBase * resolvedBottomPaddingRatio : 0;

  return {
    min: yAxisMinOverride ?? (isPercentageMetric(metricKey) ? 0 : rawYAxisMin - yAxisBottomPadding),
    max: yAxisMaxOverride ?? (isPercentageMetric(metricKey) ? 100 : rawYAxisMax + yAxisTopPadding),
  };
};

const buildSegmentStyles = (data: MiniMetricLineChartDatum[]): boolean[] => {
  if (data.length < 2) return [];
  const styles: boolean[] = [];
  for (let index = 0; index < data.length - 1; index += 1) {
    const current = data[index];
    const next = data[index + 1];
    styles.push(current.measuredValue === null || next.measuredValue === null);
  }
  return styles;
};

const buildStyledSegments = (points: PathPoint[], dashedIntervals: boolean[]): StyledSegment[] => {
  if (points.length === 0) return [];
  if (points.length === 1) return [{ dashed: false, points }];

  const segments: StyledSegment[] = [];
  let currentStyle = dashedIntervals[0] ?? false;
  let currentPoints: PathPoint[] = [points[0], points[1]];

  for (let index = 1; index < dashedIntervals.length; index += 1) {
    const nextStyle = dashedIntervals[index] ?? false;
    const nextPoint = points[index + 1] as PathPoint;

    if (nextStyle === currentStyle) {
      currentPoints.push(nextPoint);
      continue;
    }

    segments.push({ dashed: currentStyle, points: currentPoints });
    currentStyle = nextStyle;
    currentPoints = [points[index], nextPoint];
  }

  segments.push({ dashed: currentStyle, points: currentPoints });
  return segments;
};

const buildPath = (points: PathPoint[]): string | null => {
  if (points.length < 2) return null;
  if (points.length === 2) {
    const [start, end] = points;
    return `M${start.x},${start.y} L${end.x},${end.y}`;
  }

  let path = `M${points[0].x},${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[Math.max(0, index - 1)] as PathPoint;
    const current = points[index] as PathPoint;
    const next = points[index + 1] as PathPoint;
    const following = points[Math.min(points.length - 1, index + 2)] as PathPoint;

    // Catmull-Rom to cubic Bezier conversion keeps the smoothed line passing through each point.
    const controlPoint1X = current.x + (next.x - previous.x) / 6;
    const controlPoint1Y = current.y + (next.y - previous.y) / 6;
    const controlPoint2X = next.x - (following.x - current.x) / 6;
    const controlPoint2Y = next.y - (following.y - current.y) / 6;

    path += ` C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${next.x},${next.y}`;
  }
  return path;
};

export const MiniMetricLineChart = ({
  data,
  metricKey,
  color,
  backgroundColor,
  showPlotAreaBackground = false,
  showPlotAreaFrame = false,
  height,
  marginLeft,
  marginRight = 16,
  marginTop = 16,
  marginBottom = 14,
  yAxisWidth,
  axisStrokeWidth = 1,
  hideXAxisLabels = false,
  hideYAxisLabels = false,
  hideXAxisTicks = false,
  hideYAxisTicks = false,
  showAllXAxisLabels = false,
  xAxisHeight,
  yAxisTickNumber,
  yAxisTickInterval,
  yDomainPaddingRatio = 0,
  yDomainPaddingTopRatio,
  yDomainPaddingBottomRatio,
  yAxisMin,
  yAxisMax,
  showHorizontalGrid = false,
  hideXAxisLine = true,
  hideYAxisLine = true,
  tooltipLabelFormatter,
  onSelectBar,
  lineStrokeWidth = 2.5,
  dashedSegmentOpacity = 0.65,
}: MiniMetricLineChartProps): ReactElement => {
  const theme = useTheme();
  const clipPathId = useId().replace(/:/g, '-');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [plotBounds, setPlotBounds] = useState<PlotBounds | null>(null);
  const [hoveredDataIndex, setHoveredDataIndex] = useState<number | null>(null);
  const edgeMarkerInset = 6;
  const isolatedPointRadius = 3;
  const hoverPointRadius = 4;
  const overlayClipPadding = Math.max(lineStrokeWidth / 2, isolatedPointRadius, hoverPointRadius);
  const resolvedStrokeColor = color ?? resolveMiniMetricFallbackLineColor(theme);
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const resolvedXAxisHeight = xAxisHeight ?? (hideXAxisLabels && hideXAxisTicks ? 16 : 24);
  const effectiveMarginLeft = marginLeft + edgeMarkerInset;
  const effectiveMarginRight = marginRight + edgeMarkerInset;
  const plotLeft = Math.max(0, yAxisWidth + effectiveMarginLeft);
  const plotTop = Math.max(0, marginTop);
  const domain = useMemo(
    () =>
      resolveDomain(
        data,
        metricKey,
        yDomainPaddingRatio,
        yDomainPaddingTopRatio,
        yDomainPaddingBottomRatio,
        yAxisMin,
        yAxisMax,
      ),
    [
      data,
      metricKey,
      yAxisMax,
      yAxisMin,
      yDomainPaddingBottomRatio,
      yDomainPaddingRatio,
      yDomainPaddingTopRatio,
    ],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const updateBounds = (): void => {
      const nextContainerWidth = container.clientWidth;
      const nextPlotWidth = Math.max(0, nextContainerWidth - plotLeft - effectiveMarginRight);
      const nextPlotHeight = Math.max(0, height - plotTop - marginBottom - resolvedXAxisHeight);
      setPlotBounds((currentBounds) => {
        if (currentBounds?.width === nextPlotWidth && currentBounds.height === nextPlotHeight) {
          return currentBounds;
        }
        return {
          width: nextPlotWidth,
          height: nextPlotHeight,
        };
      });
    };
    updateBounds();
    const resizeObserver = new ResizeObserver(() => {
      updateBounds();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [effectiveMarginRight, height, marginBottom, plotLeft, plotTop, resolvedXAxisHeight]);

  const lineSegments = useMemo(() => {
    if (!plotBounds || data.length === 0) return [];
    const { width, height: plotHeight } = plotBounds;
    const yRange = domain.max - domain.min || 1;
    const points = data.map<PathPoint>((point, index) => {
      const x = data.length === 1 ? width / 2 : (index / Math.max(1, data.length - 1)) * width;
      const y = plotHeight - ((point.carryForwardValue - domain.min) / yRange) * plotHeight;
      return { x, y };
    });

    return buildStyledSegments(points, buildSegmentStyles(data))
      .map((segment) => ({
        ...segment,
        path: buildPath(segment.points),
      }))
      .filter((segment) => segment.path !== null);
  }, [data, domain.max, domain.min, plotBounds]);

  const measuredPoints = useMemo<MeasuredPoint[]>(() => {
    if (!plotBounds || data.length === 0) return [];
    const { width, height: plotHeight } = plotBounds;
    const yRange = domain.max - domain.min || 1;

    return data.flatMap<MeasuredPoint>((point, index) => {
      if (point.measuredValue === null) {
        return [];
      }
      const previousMeasuredValue = index > 0 ? data[index - 1].measuredValue : null;
      const nextMeasuredValue = index < data.length - 1 ? data[index + 1].measuredValue : null;
      const isIsolatedMeasuredPoint = previousMeasuredValue === null && nextMeasuredValue === null;

      if (!isIsolatedMeasuredPoint) {
        return [];
      }

      const x = data.length === 1 ? width / 2 : (index / Math.max(1, data.length - 1)) * width;
      const y = plotHeight - ((point.measuredValue - domain.min) / yRange) * plotHeight;

      return [
        {
          key: `${point.label}-${index}`,
          x,
          y,
        },
      ];
    });
  }, [data, domain.max, domain.min, plotBounds]);
  const hoveredPoint = useMemo<PathPoint | null>(() => {
    if (!plotBounds || hoveredDataIndex === null || data.length === 0) return null;
    const point = data[hoveredDataIndex];
    if (!Number.isFinite(point.carryForwardValue)) return null;

    const { width, height: plotHeight } = plotBounds;
    const yRange = domain.max - domain.min || 1;
    const x =
      data.length === 1 ? width / 2 : (hoveredDataIndex / Math.max(1, data.length - 1)) * width;
    const y = plotHeight - ((point.carryForwardValue - domain.min) / yRange) * plotHeight;

    return { x, y };
  }, [data, domain.max, domain.min, hoveredDataIndex, plotBounds]);
  const chartData = useMemo(
    () =>
      data.map((point) => ({
        label: point.label,
        [metricKey]: point.carryForwardValue,
      })),
    [data, metricKey],
  );
  const chartSeries = useMemo(
    () => [{ key: metricKey, label: '', color: resolvedStrokeColor }],
    [metricKey, resolvedStrokeColor],
  );
  const tooltipValueFormatter = useCallback(
    ({ dataIndex }: { dataIndex: number | null }) => {
      if (dataIndex === null) return 'No runs';
      const measuredValue = data[dataIndex]?.measuredValue ?? null;
      if (measuredValue === null) return 'No runs';
      if (metricKey === 'duration') {
        const seconds = measuredValue;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.round(seconds % 60);
        return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
      }
      if (isPercentageMetric(metricKey)) {
        return `${measuredValue.toFixed(2).replace(/\.00$/, '')}%`;
      }
      return measuredValue.toFixed(2).replace(/\.00$/, '');
    },
    [data, metricKey],
  );
  const handleMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!plotBounds || data.length === 0) {
        setHoveredDataIndex(null);
        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const xInPlot = event.clientX - bounds.left - plotLeft;
      const normalizedX = Math.min(1, Math.max(0, xInPlot / Math.max(1, plotBounds.width)));
      const nextIndex = Math.round(normalizedX * Math.max(0, data.length - 1));
      setHoveredDataIndex((currentIndex) =>
        currentIndex === nextIndex ? currentIndex : nextIndex,
      );
    },
    [data.length, plotBounds, plotLeft],
  );
  const handleMouseLeave = useCallback(() => {
    setHoveredDataIndex(null);
  }, []);

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        width: '100%',
        height: `${height}px`,
        backgroundColor: backgroundColor ?? chartPanelSurfaceBackgroundColor,
        '& .MuiLineElement-root': {
          display: 'none',
        },
        '& .MuiMarkElement-root': {
          opacity: 0,
        },
        '& .MuiMarkElement-highlighted': {
          opacity: 0,
        },
      }}
    >
      {plotBounds && (showPlotAreaBackground || showPlotAreaFrame) ? (
        <Box
          component="svg"
          sx={{
            position: 'absolute',
            left: `${plotLeft}px`,
            top: `${plotTop}px`,
            width: `${plotBounds.width}px`,
            height: `${plotBounds.height}px`,
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: 0,
          }}
          width={plotBounds.width}
          height={plotBounds.height}
          viewBox={`0 0 ${plotBounds.width} ${plotBounds.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {showPlotAreaBackground ? (
            <rect
              x={0}
              y={0}
              width={plotBounds.width}
              height={plotBounds.height}
              fill={plotAreaBackgroundColor}
            />
          ) : null}
          {showPlotAreaFrame ? (
            <>
              <line
                x1={0.5}
                y1={0}
                x2={0.5}
                y2={plotBounds.height}
                stroke={theme.app.colors.divider}
                strokeWidth={axisStrokeWidth}
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1={plotBounds.width - 0.5}
                y1={0}
                x2={plotBounds.width - 0.5}
                y2={plotBounds.height}
                stroke={theme.app.colors.divider}
                strokeWidth={axisStrokeWidth}
                vectorEffect="non-scaling-stroke"
              />
            </>
          ) : null}
        </Box>
      ) : null}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <LineChart
          data={chartData}
          series={chartSeries}
          backgroundColor="transparent"
          height={height}
          marginLeft={effectiveMarginLeft}
          marginRight={effectiveMarginRight}
          marginTop={marginTop}
          marginBottom={marginBottom}
          yAxisWidth={yAxisWidth}
          axisStrokeWidth={axisStrokeWidth}
          showLegend={false}
          hideXAxisLabels={hideXAxisLabels}
          hideYAxisLabels={hideYAxisLabels}
          hideXAxisTicks={hideXAxisTicks}
          hideYAxisTicks={hideYAxisTicks}
          showAllXAxisLabels={showAllXAxisLabels}
          xAxisHeight={resolvedXAxisHeight}
          yAxisTickNumber={yAxisTickNumber}
          yAxisTickInterval={yAxisTickInterval}
          yDomainPaddingRatio={yDomainPaddingRatio}
          yDomainPaddingTopRatio={yDomainPaddingTopRatio}
          yDomainPaddingBottomRatio={yDomainPaddingBottomRatio}
          yAxisMin={domain.min}
          yAxisMax={domain.max}
          showHorizontalGrid={showHorizontalGrid}
          showPlotAreaBackground={false}
          showPlotAreaFrame={false}
          hideXAxisLine={hideXAxisLine || showPlotAreaFrame}
          hideYAxisLine={hideYAxisLine}
          tooltipLabelFormatter={tooltipLabelFormatter}
          tooltipEmptyValueLabel="No runs"
          hideTooltipSeriesLabel
          tooltipValueFormatter={tooltipValueFormatter}
          onSelectBar={onSelectBar}
          showPoints={false}
          filledPoints={false}
        />
      </Box>
      {plotBounds ? (
        <Box
          component="svg"
          sx={{
            position: 'absolute',
            left: `${plotLeft}px`,
            top: `${plotTop}px`,
            width: `${plotBounds.width}px`,
            height: `${plotBounds.height}px`,
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: 2,
          }}
          width={plotBounds.width}
          height={plotBounds.height}
          viewBox={`0 0 ${plotBounds.width} ${plotBounds.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <clipPath id={`mini-metric-clip-${clipPathId}`}>
              <rect
                x={-overlayClipPadding}
                y={-overlayClipPadding}
                width={plotBounds.width + overlayClipPadding * 2}
                height={plotBounds.height + overlayClipPadding * 2}
              />
            </clipPath>
          </defs>
          {lineSegments.map((segment, index) => (
            <path
              key={`${metricKey}-${segment.dashed ? 'dash' : 'solid'}-${index}`}
              d={segment.path ?? ''}
              fill="none"
              stroke={
                segment.dashed
                  ? alpha(resolvedStrokeColor, dashedSegmentOpacity)
                  : resolvedStrokeColor
              }
              strokeWidth={lineStrokeWidth}
              strokeDasharray={segment.dashed ? '2.5 5' : undefined}
              strokeLinecap="round"
              strokeLinejoin="round"
              clipPath={`url(#mini-metric-clip-${clipPathId})`}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {measuredPoints.map((point) => (
            <circle
              key={point.key}
              cx={point.x}
              cy={point.y}
              r={isolatedPointRadius}
              fill={resolvedStrokeColor}
              stroke={resolvedStrokeColor}
              clipPath={`url(#mini-metric-clip-${clipPathId})`}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {hoveredPoint ? (
            <circle
              cx={hoveredPoint.x}
              cy={hoveredPoint.y}
              r={hoverPointRadius}
              fill={resolvedStrokeColor}
              stroke={resolvedStrokeColor}
              clipPath={`url(#mini-metric-clip-${clipPathId})`}
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
